import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface BarcodeScannerModalProps {
  open: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
  continuousMode?: boolean;
}

export const BarcodeScannerModal = ({
  open,
  onClose,
  onScan,
  continuousMode = false,
}: BarcodeScannerModalProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [Scanner, setScanner] = useState<any>(null);
  const [lastScanned, setLastScanned] = useState<string | null>(null);
  const [scanCount, setScanCount] = useState(0);
  const lastScannedRef = useRef<string | null>(null);

  // Dynamically import the scanner library when modal opens
  useEffect(() => {
    if (open && !Scanner) {
      setIsLoading(true);
      import('@yudiel/react-qr-scanner')
        .then((module) => {
          setScanner(() => module.Scanner);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load scanner:', err);
          setError('Impossible de charger le scanner. Veuillez utiliser un scanner USB.');
          setIsLoading(false);
        });
    }
  }, [open, Scanner]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setLastScanned(null);
      setScanCount(0);
      lastScannedRef.current = null;
    }
  }, [open]);

  const handleScan = (result: any) => {
    if (result && result[0]?.rawValue) {
      const barcode = result[0].rawValue;

      // Prevent duplicate scans of the same barcode in quick succession
      if (continuousMode && barcode === lastScannedRef.current) {
        return;
      }

      onScan(barcode);

      if (continuousMode) {
        lastScannedRef.current = barcode;
        setLastScanned(barcode);
        setScanCount((prev) => prev + 1);
        // Clear the last scanned display after 2 seconds
        setTimeout(() => {
          setLastScanned(null);
          lastScannedRef.current = null;
        }, 2000);
      }
      // Note: Don't call onClose() here - the parent component (BarcodeProductSearch)
      // handles dialog state transitions based on the scan result
    }
  };

  const handleError = (err: any) => {
    console.error('Scanner error:', err);
    setError("Erreur d'accès à la caméra. Veuillez autoriser l'accès ou utiliser un scanner USB.");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QrCodeScannerIcon />
        Scanner un code-barres
        {continuousMode && scanCount > 0 && (
          <Chip
            size="small"
            color="success"
            label={`${scanCount} scanné${scanCount > 1 ? 's' : ''}`}
            sx={{ ml: 'auto' }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box
            sx={{
              height: 300,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">Chargement du scanner...</Typography>
          </Box>
        ) : Scanner ? (
          <Box sx={{ position: 'relative' }}>
            <Scanner
              onScan={handleScan}
              onError={handleError}
              formats={['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code']}
              styles={{
                container: {
                  width: '100%',
                  height: 300,
                  borderRadius: 8,
                  overflow: 'hidden',
                },
              }}
              components={{
                audio: false,
                torch: true,
              }}
            />
            {/* Success overlay when barcode is scanned in continuous mode */}
            {continuousMode && lastScanned && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(46, 125, 50, 0.85)',
                  borderRadius: 1,
                  animation: 'fadeIn 0.2s ease-in-out',
                  '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 64, color: 'white', mb: 1 }} />
                <Typography variant="h6" color="white" fontWeight="bold">
                  Scanné !
                </Typography>
                <Typography variant="body2" color="white" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                  {lastScanned}
                </Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 2,
                color: 'text.secondary',
              }}
            >
              {continuousMode
                ? 'Scannez les produits un par un. Le scanner reste ouvert.'
                : 'Placez le code-barres dans le cadre pour le scanner'}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              height: 300,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              bgcolor: 'action.hover',
              borderRadius: 2,
            }}
          >
            <CameraAltIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography color="text.secondary" align="center">
              Scanner non disponible.
              <br />
              Utilisez un scanner USB ou saisissez le code manuellement.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {continuousMode && scanCount > 0 ? `Fermer (${scanCount})` : 'Fermer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
